import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { recordingFolders } from '@/api/recordingFolders';
import type { RecordingFolderRow } from '@/api/recordingFolders';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useEffectiveCustomerId } from '@/lib/customer-context-store';
import { useCustomerPicker } from '@/components/CustomerPicker';
import { CustomerScopeChip } from '@/components/CustomerScopeChip';
import { PageHeader } from '@/components/PageHeader';
import { formatRef } from '@/lib/formatRef';
import { cn } from '@/lib/cn';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose,
} from '@/components/ui/dialog';
import { Plus, FolderOpen, Folder } from 'lucide-react';
import { FolderDetailInner } from './FolderDetail';

const MUTATE_ROLES = ['super_admin', 'reseller_owner', 'office_manager', 'standard_user'];

// ── Root page ─────────────────────────────────────────────────────────────────

export function FoldersList() {
  const user = useCurrentUser();
  const effectiveCustomerId = useEffectiveCustomerId();
  const canMutate = MUTATE_ROLES.includes(user.role);
  const [createOpen, setCreateOpen] = useState(false);
  const [search, setSearch] = useState('');

  // Selected folder for the 2-pane view (Sam 2026-05-20). Persisted in
  // sessionStorage so navigating away + back lands on the same selection.
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(() => {
    if (typeof window === 'undefined') return null;
    return window.sessionStorage.getItem('folders:selectedFolderId') || null;
  });
  function pickFolder(id: string | null) {
    setSelectedFolderId(id);
    if (typeof window !== 'undefined') {
      if (id) window.sessionStorage.setItem('folders:selectedFolderId', id);
      else window.sessionStorage.removeItem('folders:selectedFolderId');
    }
  }

  const { data, isLoading } = useQuery({
    queryKey: ['allFolders', effectiveCustomerId],
    queryFn: () => recordingFolders.list({
      limit: 500,
      customer_id: effectiveCustomerId ?? undefined,
    }),
  });

  const allFolders = data?.rows ?? [];
  const folders = search.trim()
    ? allFolders.filter((f) =>
        f.name.toLowerCase().includes(search.trim().toLowerCase()),
      )
    : allFolders;
  const selectedFolder = allFolders.find((f) => f.id === selectedFolderId) ?? null;

  return (
    <>
      <PageHeader
        eyebrow="Recordings"
        title="Folders"
        description="Pick a folder on the left to see its recordings. The IVR node type controls how recordings are played — any folder works with any node type."
        actions={canMutate && (
          <Button size="sm" onClick={() => setCreateOpen(true)}>
            <Plus className="h-4 w-4 mr-1.5" />
            New folder
          </Button>
        )}
      />

      <CustomerScopeChip />

      {isLoading ? (
        <div className="text-sm text-slate-400">Loading…</div>
      ) : allFolders.length === 0 ? (
        <EmptyState onCreate={canMutate ? () => setCreateOpen(true) : undefined} />
      ) : (
        <div className="flex gap-4 items-start">
          {/* Sidebar — folder list with counts */}
          <aside
            className="w-72 shrink-0 rounded-md border border-slate-200 bg-white"
            aria-label="Folders"
          >
            <div className="border-b border-slate-100 p-2">
              <Input
                placeholder="Search folders…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="h-8 text-sm"
              />
            </div>
            {folders.length === 0 ? (
              <div className="px-3 py-4 text-center text-xs text-slate-400">
                No folders match &ldquo;{search}&rdquo;.
              </div>
            ) : (
              <ul className="max-h-[70vh] overflow-y-auto py-1">
                {folders.map((f) => {
                  const active = selectedFolderId === f.id;
                  return (
                    <li key={f.id}>
                      <button
                        type="button"
                        onClick={() => pickFolder(f.id)}
                        className={cn(
                          'flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm transition-colors',
                          active
                            ? 'bg-blue-50 text-blue-700 font-medium'
                            : 'text-slate-700 hover:bg-slate-50',
                        )}
                        title={f.description ?? f.name}
                      >
                        <span className="flex items-center gap-2 min-w-0">
                          {active
                            ? <FolderOpen className="h-4 w-4 shrink-0 text-blue-500" />
                            : <Folder className="h-4 w-4 shrink-0 text-slate-400" />}
                          <span className="min-w-0">
                            <span className="block truncate">{f.name}</span>
                            <span className="block text-[10px] font-mono text-slate-400">
                              {formatRef(f.ref_number, 'FLD')}
                            </span>
                          </span>
                        </span>
                        <span className="text-xs tabular-nums text-slate-500 shrink-0">
                          {f.recording_count ?? '0'}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </aside>

          {/* Main pane — full FolderDetailInner (Add / reorder / settings)
              renders inline. `key={folder.id}` resets the inner state
              (pagination, settings-open, etc.) when the user switches
              between folders in the left list. */}
          <div className="flex-1 min-w-0">
            {selectedFolder ? (
              <FolderDetailInner
                key={selectedFolder.id}
                folder={selectedFolder}
                embedded
              />
            ) : (
              <Card>
                <CardContent className="py-12 text-center text-sm text-muted-foreground">
                  <FolderOpen className="mx-auto h-8 w-8 text-slate-300 mb-3" />
                  Select a folder from the list to see its recordings.
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      )}

      <CreateFolderDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onCreated={(folder) => {
          pickFolder(folder.id);
        }}
      />
    </>
  );
}

// ── Empty state ───────────────────────────────────────────────────────────────

function EmptyState({ onCreate }: { onCreate?: () => void }) {
  return (
    <Card>
      <CardContent className="py-12 text-center">
        <FolderOpen className="mx-auto h-8 w-8 text-slate-300 mb-3" />
        <p className="text-sm font-medium text-slate-500">No folders yet</p>
        <p className="text-xs text-muted-foreground mt-1 max-w-md mx-auto">
          Create a folder, add recordings, then point an IVR node at it. The node type
          (Bulletin, Series, etc.) controls how callers hear the recordings.
        </p>
        {onCreate && (
          <Button size="sm" className="mt-4" onClick={onCreate}>
            <Plus className="h-4 w-4 mr-1.5" />
            Create the first folder
          </Button>
        )}
      </CardContent>
    </Card>
  );
}

// ── Create folder dialog ──────────────────────────────────────────────────────

function CreateFolderDialog({
  open, onOpenChange, onCreated,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  onCreated: (folder: RecordingFolderRow) => void;
}) {
  const qc = useQueryClient();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [customerId, setCustomerId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { effectiveCustomerId, element: customerPicker, isPickerVisible } =
    useCustomerPicker(customerId, setCustomerId, { required: true });
  void isPickerVisible;

  const create = useMutation({
    mutationFn: () => recordingFolders.create({
      name: name.trim(),
      description: description.trim() || null,
      ...(effectiveCustomerId ? { customer_id: effectiveCustomerId } : {}),
    }),
    onSuccess: (row) => {
      qc.invalidateQueries({ queryKey: ['allFolders', effectiveCustomerId] });
      setName(''); setDescription(''); setCustomerId(null); setError(null);
      onOpenChange(false);
      onCreated(row);
    },
    onError: (err: unknown) => {
      setError(err instanceof Error ? err.message : 'Create failed.');
    },
  });

  const canSubmit = name.trim().length > 0 && effectiveCustomerId !== null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>New folder</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          {customerPicker}
          <CustomerScopeChip />
          <div className="space-y-1">
            <Label htmlFor="fld-name">Name</Label>
            <Input
              id="fld-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Weekly announcements"
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="fld-desc">Description (optional)</Label>
            <Textarea
              id="fld-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              maxLength={1000}
              placeholder="What goes in this folder…"
            />
          </div>
          {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
        </div>
        <DialogFooter>
          <DialogClose asChild>
            <Button type="button" variant="outline" disabled={create.isPending}>Cancel</Button>
          </DialogClose>
          <Button
            type="button"
            disabled={!canSubmit || create.isPending}
            onClick={() => create.mutate()}
          >
            {create.isPending ? 'Creating…' : 'Create folder'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

